import { changed, noOp, type Command, type CommandExecution } from './commands';
import { createId, type EditorDocument } from './model';

export const ARTBOARDS_EXTENSION = 'maipo:artboards';
export const EXPORT_ASSETS_EXTENSION = 'maipo:export-assets';
export const SLICES_EXTENSION = 'maipo:slices';

export interface Artboard {
  id: string;
  name: string;
  rect: { x: number; y: number; width: number; height: number };
  background: 'transparent' | 'white';
}

export interface ExportAsset {
  id: string;
  name: string;
  source: { kind: 'document' } | { kind: 'layer'; layerId: string };
  format: 'png' | 'jpeg';
  scale: 1 | 2 | 3;
  trim: boolean;
}

export interface Slice {
  id: string;
  name: string;
  rect: { x: number; y: number; width: number; height: number };
  format: 'png' | 'jpeg';
  quality: number;
}

function validArtboard(value: unknown): value is Artboard {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<Artboard>;
  return typeof item.id === 'string' && typeof item.name === 'string'
    && (item.background === 'transparent' || item.background === 'white')
    && Boolean(item.rect && [item.rect.x, item.rect.y, item.rect.width, item.rect.height].every(Number.isFinite)
      && item.rect.width > 0 && item.rect.height > 0);
}

function validAsset(value: unknown): value is ExportAsset {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<ExportAsset>;
  return typeof item.id === 'string' && typeof item.name === 'string'
    && (item.format === 'png' || item.format === 'jpeg')
    && (item.scale === 1 || item.scale === 2 || item.scale === 3)
    && typeof item.trim === 'boolean'
    && Boolean(item.source && (item.source.kind === 'document' || (item.source.kind === 'layer' && typeof item.source.layerId === 'string')));
}

function validSlice(value: unknown): value is Slice {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<Slice>;
  return typeof item.id === 'string' && typeof item.name === 'string'
    && (item.format === 'png' || item.format === 'jpeg')
    && Number.isFinite(item.quality) && item.quality! >= 1 && item.quality! <= 100
    && Boolean(item.rect && [item.rect.x, item.rect.y, item.rect.width, item.rect.height].every(Number.isFinite)
      && item.rect.width > 0 && item.rect.height > 0);
}

export function artboards(document: EditorDocument): Artboard[] {
  const value = document.extensions[ARTBOARDS_EXTENSION];
  return Array.isArray(value) ? value.filter(validArtboard).map((item) => structuredClone(item)) : [];
}

export function exportAssets(document: EditorDocument): ExportAsset[] {
  const value = document.extensions[EXPORT_ASSETS_EXTENSION];
  return Array.isArray(value) ? value.filter(validAsset).map((item) => structuredClone(item)) : [];
}

export function slices(document: EditorDocument): Slice[] {
  const value = document.extensions[SLICES_EXTENSION];
  return Array.isArray(value) ? value.filter(validSlice).map((item) => structuredClone(item)) : [];
}

export function createArtboard(document: EditorDocument): Artboard {
  const existing = artboards(document);
  return {
    id: createId('artboard'),
    name: `Artboard ${existing.length + 1}`,
    rect: { x: 0, y: 0, width: document.width, height: document.height },
    background: 'transparent',
  };
}

export function createExportAsset(document: EditorDocument, layerId?: string): ExportAsset {
  const existing = exportAssets(document);
  return {
    id: createId('asset'),
    name: layerId ? `Layer ${existing.length + 1}` : document.name,
    source: layerId ? { kind: 'layer', layerId } : { kind: 'document' },
    format: 'png',
    scale: 1,
    trim: Boolean(layerId),
  };
}

export function createSlice(document: EditorDocument, rect = { x: 0, y: 0, width: document.width, height: document.height }, name?: string): Slice {
  const existing = slices(document);
  return {
    id: createId('slice'),
    name: name?.trim() || `Slice ${existing.length + 1}`,
    rect: { ...rect },
    format: 'png',
    quality: 90,
  };
}

export function slicesFromGuides(document: EditorDocument, maximum = 64): Slice[] {
  const xs = [...new Set([0, document.width, ...(document.guides ?? []).filter((guide) => guide.axis === 'vertical').map((guide) => Math.max(0, Math.min(document.width, guide.position)))])].sort((left, right) => left - right);
  const ys = [...new Set([0, document.height, ...(document.guides ?? []).filter((guide) => guide.axis === 'horizontal').map((guide) => Math.max(0, Math.min(document.height, guide.position)))])].sort((left, right) => left - right);
  const output: Slice[] = [];
  for (let y = 0; y < ys.length - 1; y += 1) for (let x = 0; x < xs.length - 1; x += 1) {
    const left = xs[x]; const right = xs[x + 1]; const top = ys[y]; const bottom = ys[y + 1];
    if (left === undefined || right === undefined || top === undefined || bottom === undefined || right <= left || bottom <= top) continue;
    if (output.length >= maximum) throw new Error(`Guide layout creates more than ${maximum} slices.`);
    output.push({
      id: createId('slice'),
      name: `Slice ${output.length + 1}`,
      rect: { x: left, y: top, width: right - left, height: bottom - top },
      format: 'png',
      quality: 90,
    });
  }
  return output;
}

class SetExtensionListCommand<T> implements Command {
  private before?: unknown;
  private hadBefore = false;
  constructor(readonly label: string, private readonly key: string, private readonly values: readonly T[]) {}
  retainedBytes(): number { return 128 + JSON.stringify(this.values).length * 2 + JSON.stringify(this.before ?? null).length * 2; }
  execute(document: EditorDocument): CommandExecution {
    if (JSON.stringify(document.extensions[this.key] ?? []) === JSON.stringify(this.values)) return noOp();
    if (this.before === undefined) {
      this.hadBefore = Object.hasOwn(document.extensions, this.key);
      this.before = structuredClone(document.extensions[this.key] ?? null);
    }
    if (this.values.length) document.extensions[this.key] = structuredClone(this.values);
    else delete document.extensions[this.key];
    return changed();
  }
  undo(document: EditorDocument): void {
    if (this.hadBefore) document.extensions[this.key] = structuredClone(this.before);
    else delete document.extensions[this.key];
  }
}

export class SetArtboardsCommand extends SetExtensionListCommand<Artboard> {
  constructor(values: readonly Artboard[], label = 'Update artboards') { super(label, ARTBOARDS_EXTENSION, values); }
}

export class SetExportAssetsCommand extends SetExtensionListCommand<ExportAsset> {
  constructor(values: readonly ExportAsset[], label = 'Update export assets') { super(label, EXPORT_ASSETS_EXTENSION, values); }
}

export class SetSlicesCommand extends SetExtensionListCommand<Slice> {
  constructor(values: readonly Slice[], label = 'Update slices') { super(label, SLICES_EXTENSION, values); }
}
